import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Bot,
  BarChart2,
  Eye,
  MessageSquare,
  Cpu,
  CheckCircle,
  ArrowRight,
  Check,
  X,
  Zap,
  Shield,
  RefreshCw,
} from "lucide-react";
import "./AIServices.css";
import { subscribeToNewsletter } from "./newsletterService";

export default function AIServices() {
  const [activeTab, setActiveTab] = useState("models");
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fallbackPalette = [
    "#1A9E96",
    "#0D7A73",
    "#6366f1",
    "#f97316",
    "#0ea5e9",
    "#8b5cf6",
  ];

  const getInitials = (name) =>
    name
      .split(/\s+/)
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const makeFallbackSvg = (label, color) => {
    const safeLabel = getInitials(label);
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
        <defs>
          <linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stop-color="${color}" stop-opacity="0.9" />
            <stop offset="100%" stop-color="${color}" stop-opacity="0.7" />
          </linearGradient>
        </defs>
        <rect x="4" y="4" width="56" height="56" rx="14" fill="url(#g)" />
        <text x="32" y="38" text-anchor="middle" font-family="Arial, sans-serif" font-size="20" font-weight="700" fill="#ffffff">
          ${safeLabel}
        </text>
      </svg>
    `;
    return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  };

  const startProject = () => {
    window.location.href = "/#contact";
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const email = form.newsletterEmail?.value || "";
    const result = subscribeToNewsletter(email);
    window.alert(result.message);
    if (result.ok) form.reset();
  };

  // ─── Data ────────────────────────────────────────────────────────────────────

  const technologies = {
    models: [
      {
        name: "OpenAI GPT",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/openai/openai-original.svg",
        color: "#10a37f",
      },
      {
        name: "Claude AI",
        logo: "https://upload.wikimedia.org/wikipedia/commons/8/8a/Claude_AI_logo.svg",
        color: "#c7622a",
      },
      {
        name: "Gemini",
        logo: "https://www.gstatic.com/lamda/images/gemini_sparkle_v002_d4735304ff6292a690345.svg",
        color: "#4285F4",
      },
      {
        name: "LLaMA",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/meta/meta-original.svg",
        color: "#0467DF",
      },
      {
        name: "Hugging Face",
        logo: "https://huggingface.co/front/assets/huggingface_logo-noborder.svg",
        color: "#FFD21E",
      },
      {
        name: "Mistral",
        logo: "https://mistral.ai/images/news/announcing-mistral.png",
        color: "#f7931a",
      },
    ],
    mltools: [
      {
        name: "TensorFlow",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tensorflow/tensorflow-original.svg",
        color: "#FF6F00",
      },
      {
        name: "PyTorch",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pytorch/pytorch-original.svg",
        color: "#EE4C2C",
      },
      {
        name: "scikit-learn",
        logo: "https://upload.wikimedia.org/wikipedia/commons/0/05/Scikit_learn_logo_small.svg",
        color: "#F89939",
      },
      {
        name: "LangChain",
        logo: "https://avatars.githubusercontent.com/u/126733545?s=200&v=4",
        color: "#1C3C3C",
      },
      {
        name: "Pandas",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg",
        color: "#150458",
      },
      {
        name: "Python",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg",
        color: "#3776AB",
      },
    ],
    infra: [
      {
        name: "AWS SageMaker",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg",
        color: "#FF9900",
      },
      {
        name: "Azure AI",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg",
        color: "#0078D4",
      },
      {
        name: "Google Cloud",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/googlecloud/googlecloud-original.svg",
        color: "#4285F4",
      },
      {
        name: "Docker",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg",
        color: "#2496ED",
      },
      {
        name: "Kubernetes",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg",
        color: "#326CE5",
      },
      {
        name: "FastAPI",
        logo: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/fastapi/fastapi-original.svg",
        color: "#009688",
      },
    ],
  };

  const whatWeOffer = [
    {
      icon: <Bot className="w-6 h-6" />,
      title: "AI Chatbots & Assistants",
      description:
        "Custom conversational AI trained on your business data — for customer support, internal helpdesks, and automated query resolution.",
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Machine Learning Models",
      description:
        "End-to-end ML pipelines for prediction, classification, anomaly detection, and recommendation — built and deployed on your infrastructure.",
    },
    {
      icon: <BarChart2 className="w-6 h-6" />,
      title: "AI-Powered Analytics",
      description:
        "Transform raw data into actionable intelligence with dashboards, forecasting models, and natural language reporting.",
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "Computer Vision",
      description:
        "Object detection, document processing, quality inspection, and visual search systems for manufacturing, retail, and logistics.",
    },
    {
      icon: <MessageSquare className="w-6 h-6" />,
      title: "NLP & Document AI",
      description:
        "Extract, classify, and summarize content from contracts, invoices, reports, and unstructured documents automatically.",
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "AI Integration & Automation",
      description:
        "Embed AI capabilities into your existing systems, workflows, and applications via clean, maintainable APIs.",
    },
  ];

  const whyChooseUs = [
    "Custom models trained on your business data",
    "Explainable AI — not a black box",
    "End-to-end delivery from data to deployment",
    "Integrates with your existing tech stack",
    "POPIA-aware data handling",
    "Ongoing model monitoring and retraining",
    "No lock-in — you own the models",
    "Local support, South African context",
  ];

  const packages = [
    {
      name: "AI Starter",
      price: "From R15,000",
      ideal: "Chatbots & quick wins",
      features: [
        { text: "Single AI feature / chatbot", included: true },
        { text: "Pre-trained model integration", included: true },
        { text: "API endpoint delivery", included: true },
        { text: "Basic prompt engineering", included: true },
        { text: "2 months support", included: true },
        { text: "Custom model fine-tuning", included: false },
        { text: "Data pipeline setup", included: false },
        { text: "Dedicated MLOps", included: false },
      ],
      popular: false,
    },
    {
      name: "AI Business",
      price: "From R45,000",
      ideal: "Intelligent business systems",
      features: [
        { text: "Multi-feature AI solution", included: true },
        { text: "Custom fine-tuned model", included: true },
        { text: "Data pipeline & preprocessing", included: true },
        { text: "Dashboard & reporting layer", included: true },
        { text: "6 months support", included: true },
        { text: "Performance monitoring", included: true },
        { text: "System integrations", included: true },
        { text: "Model retraining cycles", included: true },
      ],
      popular: true,
    },
    {
      name: "AI Enterprise",
      price: "From R120,000",
      ideal: "Full AI transformation",
      features: [
        { text: "Full AI product suite", included: true },
        { text: "Proprietary model training", included: true },
        { text: "MLOps infrastructure", included: true },
        { text: "On-premise or cloud deployment", included: true },
        { text: "12+ months support", included: true },
        { text: "Compliance & audit readiness", included: true },
        { text: "Team training & documentation", included: true },
        { text: "Dedicated AI engineering team", included: true },
      ],
      popular: false,
    },
  ];

  const process = [
    {
      step: "1",
      title: "Discovery & Data Audit",
      description:
        "We assess your business goals, available data, and existing systems to define what AI can realistically deliver.",
    },
    {
      step: "2",
      title: "Solution Design",
      description:
        "Architecture planning for model selection, data pipelines, integration points, and deployment strategy.",
    },
    {
      step: "3",
      title: "Model Development",
      description:
        "Training, fine-tuning, and validating models against your real business data with iterative review.",
    },
    {
      step: "4",
      title: "Integration & Testing",
      description:
        "Embedding AI into your workflows and systems, followed by accuracy, performance, and edge-case testing.",
    },
    {
      step: "5",
      title: "Deployment & Monitoring",
      description:
        "Production rollout with live monitoring, drift detection, and scheduled retraining to keep models accurate.",
    },
  ];

  const useCases = [
    {
      icon: "building",
      name: "Finance & Insurance",
      description: "Fraud detection, credit scoring, document processing",
    },
    {
      icon: "stethoscope",
      name: "Healthcare",
      description: "Diagnostic support, patient triage, clinical note summarisation",
    },
    {
      icon: "industry",
      name: "Manufacturing",
      description: "Visual quality inspection, predictive maintenance, yield optimisation",
    },
    {
      icon: "truck",
      name: "Logistics",
      description: "Route optimisation, demand forecasting, shipment anomaly detection",
    },
    {
      icon: "graduation-cap",
      name: "Education",
      description: "Adaptive learning paths, automated grading, student analytics",
    },
    {
      icon: "shopping-bag",
      name: "Retail & E-Commerce",
      description: "Product recommendations, inventory forecasting, sentiment analysis",
    },
  ];

  const benefits = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Speed at Scale",
      description:
        "AI handles repetitive, high-volume tasks in milliseconds — freeing your team for work that requires human judgement.",
    },
    {
      icon: <BarChart2 className="w-8 h-8" />,
      title: "Data-Driven Decisions",
      description:
        "Move beyond gut feel. AI surfaces patterns in your data that would take analysts weeks to find manually.",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure by Design",
      description:
        "Every solution is built with data privacy, access control, and POPIA compliance considerations from day one.",
    },
    {
      icon: <RefreshCw className="w-8 h-8" />,
      title: "Continuously Improving",
      description:
        "Unlike static software, AI models can be retrained on new data — so accuracy improves as your business grows.",
    },
  ];

  const showcase = [
    {
      src: "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=1200&q=80",
      title: "Intelligent Automation Pipelines",
      tag: "ML & Automation",
    },
    {
      src: "https://images.unsplash.com/photo-1620712943543-bcc4688e7485?auto=format&fit=crop&w=1200&q=80",
      title: "Conversational AI Interfaces",
      tag: "NLP & Chatbots",
    },
    {
      src: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=1200&q=80",
      title: "AI-Powered Business Analytics",
      tag: "Data Intelligence",
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="ai-services-page">
      {/* Navbar */}
      <nav className={`navbar ${isScrolled ? "scrolled" : ""}`}>
        <div className="container navbar-container">
          <a href="/" className="navbar-brand">
            <img
              src="/TechXM-logo.png"
              alt="TECHXM logo"
              className="brand-logo-mark"
              width="30"
              height="30"
            />
          </a>

          <div className={`navbar-menu ${mobileMenuOpen ? "active" : ""}`}>
            <div className="services-dropdown">
              <div className="navbar-link active-link">Services</div>
              <div className="dropdown-menu">
                <Link to="/web-development" className="dropdown-item">
                  <i className="fas fa-laptop-code"></i>
                  Web Development Services
                </Link>
                <Link to="/mobile-app" className="dropdown-item">
                  <i className="fas fa-mobile-alt"></i>
                  Mobile App Development
                </Link>
                <Link to="/custom-software" className="dropdown-item">
                  <i className="fas fa-cogs"></i>
                  Custom Software Development
                </Link>
                <Link to="/api-backend" className="dropdown-item">
                  <i className="fas fa-plug"></i>
                  API & Backend Services
                </Link>
                <Link to="/ai-services" className="dropdown-item active-link">
                  <i className="fas fa-brain"></i>
                  AI & Machine Learning
                </Link>
                <Link to="/maintenance" className="dropdown-item">
                  <i className="fas fa-tools"></i>
                  Website & App Maintenance
                </Link>
              </div>
            </div>
            <a href="/#about" className="navbar-link">About</a>
            <a href="/#faq" className="navbar-link">FAQ</a>
            <a href="/#contact" className="navbar-link">Contact</a>
            <Link to="/track-project" className="navbar-link">Track Project</Link>
            <button className="btn btn-primary nav-consult-btn" onClick={startProject}>
              Request Consultation
            </button>
          </div>

          <button
            className="navbar-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
          </button>
        </div>
      </nav>

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="ai-hero">
        <div className="ai-container">
          <div className="ai-breadcrumb">
            <span>Home</span> / <span>Services</span> /{" "}
            <span className="font-semibold">AI & Machine Learning</span>
          </div>
          <div className="ai-hero-badge">
            <span className="ai-hero-badge-dot"></span>
            Now Available
          </div>
          <h1 className="ai-hero-title">
            AI & Machine Learning <br />
            <span className="ai-hero-title-accent">Solutions</span>
          </h1>
          <p className="ai-hero-subtitle">
            Practical, production-ready AI built for South African businesses.
            From intelligent chatbots and predictive models to document
            automation and computer vision — we deliver AI that works in the
            real world.
          </p>
        </div>
        <div className="ai-hero-grid-overlay" aria-hidden="true"></div>
      </section>

      {/* ── Overview ─────────────────────────────────────────────────────────── */}
      <section className="ai-overview">
        <div className="ai-container">
          <div className="ai-overview-grid">
            <div>
              <h2 className="ai-section-title">
                AI That Fits Your Business, Not the Other Way Around
              </h2>
              <p className="ai-section-text">
                Most AI projects fail not because the technology doesn't work
                — but because solutions are built without understanding the
                business. We start with your workflows, your data, and your
                goals before writing a single line of model code.
              </p>
              <p className="ai-section-text">
                Whether you're automating a manual process, building a
                customer-facing assistant, or extracting value from years of
                untapped data, our team designs AI systems that are
                transparent, maintainable, and grounded in your operating
                context across Johannesburg and South Africa.
              </p>
            </div>
            <div className="ai-why-box">
              <h3 className="ai-why-title">Why AI With TechXM?</h3>
              <div className="ai-why-list">
                {whyChooseUs.map((item, idx) => (
                  <div key={idx} className="ai-why-item">
                    <CheckCircle className="w-5 h-5 flex-shrink-0 ai-check-icon" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Showcase ─────────────────────────────────────────────────────────── */}
      <section className="ai-showcase">
        <div className="ai-container">
          <h2 className="ai-showcase-title">Applied AI, Delivered</h2>
          <p className="ai-showcase-subtitle">
            From intelligent interfaces to backend decision engines — we build
            AI that's visible, measurable, and ready for production.
          </p>
          <div className="ai-showcase-grid">
            {showcase.map((item, idx) => (
              <article key={idx} className="ai-showcase-card">
                <img src={item.src} alt={item.title} className="ai-showcase-image" />
                <div className="ai-showcase-overlay">
                  <span className="ai-showcase-tag">{item.tag}</span>
                  <h3 className="ai-showcase-card-title">{item.title}</h3>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Benefits ─────────────────────────────────────────────────────────── */}
      <section className="ai-benefits">
        <div className="ai-container">
          <h2 className="ai-benefits-title">The Intelligent Business Advantage</h2>
          <div className="ai-benefits-grid">
            {benefits.map((b, idx) => (
              <div key={idx} className="ai-benefit-card">
                <div className="ai-benefit-icon">{b.icon}</div>
                <h3 className="ai-benefit-card-title">{b.title}</h3>
                <p className="ai-benefit-card-desc">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Solutions ────────────────────────────────────────────────────────── */}
      <section className="ai-solutions" id="ai-solutions">
        <div className="ai-container">
          <h2 className="ai-solutions-title">AI Solutions We Build</h2>
          <p className="ai-solutions-subtitle">
            A full spectrum of applied AI — from quick integrations to custom-trained models
          </p>
          <div className="ai-solutions-grid">
            {whatWeOffer.map((item, idx) => (
              <div key={idx} className="ai-solution-card">
                <div className="ai-solution-icon">{item.icon}</div>
                <h3 className="ai-solution-card-title">{item.title}</h3>
                <p className="ai-solution-card-desc">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ────────────────────────────────────────────────────────── */}
      <section className="ai-industries">
        <div className="ai-container">
          <h2 className="ai-industries-title">Industries We Serve</h2>
          <p className="ai-industries-subtitle">
            Deep domain knowledge across sectors where AI delivers measurable ROI
          </p>
          <div className="ai-industries-grid">
            {useCases.map((uc, idx) => (
              <div key={idx} className="ai-industry-card">
                <div className="ai-industry-icon">
                  <i className={`fas fa-${uc.icon}`}></i>
                </div>
                <h3 className="ai-industry-name">{uc.name}</h3>
                <p className="ai-industry-desc">{uc.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Technologies ─────────────────────────────────────────────────────── */}
      <section className="ai-technologies">
        <div className="ai-container">
          <h2 className="ai-tech-title">Our AI Technology Stack</h2>
          <p className="ai-tech-subtitle">
            Best-in-class models, frameworks, and infrastructure for reliable AI delivery
          </p>

          <div className="ai-tab-container">
            {Object.keys(technologies).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`ai-tab-btn ${activeTab === tab ? "active" : ""}`}
              >
                {tab === "models"
                  ? "AI Models & LLMs"
                  : tab === "mltools"
                  ? "ML Frameworks"
                  : "Cloud & Infrastructure"}
              </button>
            ))}
          </div>

          <div className="ai-tech-grid">
            {technologies[activeTab].map((tech, idx) => (
              <div key={idx} className="ai-tech-card">
                <div className="ai-tech-logo">
                  <img
                    src={tech.logo}
                    alt={tech.name}
                    onError={(e) => {
                      e.target.onerror = null;
                      const color =
                        tech.color ||
                        fallbackPalette[
                          Math.abs(
                            tech.name
                              .split("")
                              .reduce((acc, ch) => acc + ch.charCodeAt(0), 0)
                          ) % fallbackPalette.length
                        ];
                      e.target.src = makeFallbackSvg(tech.name, color);
                    }}
                  />
                </div>
                <span className="ai-tech-name">{tech.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ──────────────────────────────────────────────────────────── */}
      <section className="ai-process">
        <div className="ai-container">
          <h2 className="ai-process-title">How We Deliver AI</h2>
          <p className="ai-process-subtitle">
            A rigorous, outcome-focused process from discovery to live model
          </p>
          <div className="ai-process-steps">
            {process.map((item, idx) => (
              <div key={idx} className="ai-process-step">
                <div className="ai-step-number">{item.step}</div>
                <h3 className="ai-step-title">{item.title}</h3>
                <p className="ai-step-desc">{item.description}</p>
                {idx < process.length - 1 && (
                  <div className="ai-process-arrow">
                    <ArrowRight className="w-6 h-6" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Packages ─────────────────────────────────────────────────────────── */}
      <section className="ai-packages">
        <div className="ai-container">
          <h2 className="ai-packages-title">AI Engagement Packages</h2>
          <p className="ai-packages-subtitle">
            From a focused chatbot to a full intelligent platform — scoped to your budget
          </p>
          <div className="ai-packages-grid">
            {packages.map((pkg, idx) => (
              <div
                key={idx}
                className={`ai-package-card ${pkg.popular ? "popular" : "standard"}`}
              >
                {pkg.popular && (
                  <div className="ai-popular-badge">MOST POPULAR</div>
                )}
                <h3 className="ai-package-name">{pkg.name}</h3>
                <div className={`ai-package-price ${pkg.popular ? "popular" : ""}`}>
                  {pkg.price}
                </div>
                <p className={`ai-package-ideal ${pkg.popular ? "popular" : ""}`}>
                  {pkg.ideal}
                </p>
                <div className="ai-package-features">
                  {pkg.features.map((f, i) => (
                    <div key={i} className="ai-feature-item">
                      {f.included ? (
                        <Check
                          className={`w-5 h-5 flex-shrink-0 ${
                            pkg.popular ? "text-green-300" : "text-green-500"
                          }`}
                        />
                      ) : (
                        <X
                          className={`w-5 h-5 flex-shrink-0 ${
                            pkg.popular ? "text-emerald-300" : "text-slate-300"
                          }`}
                        />
                      )}
                      <span
                        className={`ai-feature-text ${f.included ? "included" : "excluded"} ${
                          pkg.popular ? "popular" : "standard"
                        }`}
                      >
                        {f.text}
                      </span>
                    </div>
                  ))}
                </div>
                <button
                  className={`ai-package-btn ${pkg.popular ? "popular" : "standard"}`}
                  onClick={startProject}
                >
                  Discuss Requirements
                </button>
              </div>
            ))}
          </div>
          <p className="ai-packages-note">
            All packages include model documentation and source access. No black boxes.
          </p>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────────── */}
      <section className="ai-cta">
        <div className="ai-container">
          <h2 className="ai-cta-title">Ready to Put AI to Work?</h2>
          <p className="ai-cta-subtitle">
            Let's identify where AI can save your team time, improve accuracy,
            or unlock new revenue — starting with a no-obligation conversation.
          </p>
          <div className="ai-cta-actions">
            <button className="ai-cta-btn" onClick={startProject}>
              Request Consultation <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer className="footer">
        <div className="footer-decoration"></div>
        <div className="footer-decoration"></div>

        <div className="container">
          <div className="footer-grid">
            <div>
              <div className="footer-brand">
                <img
                  src="/TechXM-logo.png"
                  alt="TECHXM logo"
                  className="brand-logo-mark"
                  width="30"
                  height="30"
                />
              </div>
              <p className="footer-about">
                We design, build, and support secure digital systems for
                organizations that require reliability, scalability, and strong
                delivery governance.
              </p>
              <div className="footer-social">
                <a
                  href="https://www.linkedin.com/company/techxm/"
                  className="social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fab fa-linkedin"></i>
                </a>
                <a href="https://www.instagram.com/techxm_za/" className="social-link" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="https://www.facebook.com/" className="social-link" target="_blank" rel="noopener noreferrer">
                  <i className="fab fa-facebook"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li><a href="/web-development"><i className="fas fa-chevron-right"></i>Web Development</a></li>
                <li><a href="/mobile-app"><i className="fas fa-chevron-right"></i>Mobile Apps</a></li>
                <li><a href="/custom-software"><i className="fas fa-chevron-right"></i>Custom Software</a></li>
                <li><a href="/api-backend"><i className="fas fa-chevron-right"></i>API & Backend</a></li>
                <li><a href="/ai-services"><i className="fas fa-chevron-right"></i>AI & Machine Learning</a></li>
                <li><a href="/maintenance"><i className="fas fa-chevron-right"></i>Maintenance</a></li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li><a href="/#about"><i className="fas fa-chevron-right"></i>About</a></li>
                <li><a href="/#faq"><i className="fas fa-chevron-right"></i>FAQ</a></li>
                <li><a href="/track-project"><i className="fas fa-chevron-right"></i>Track Project</a></li>
                <li><a href="/#contact"><i className="fas fa-chevron-right"></i>Contact</a></li>
              </ul>
            </div>

            <div>
              <h4 className="footer-heading">Contact Info</h4>
              <div className="footer-contact">
                <div className="contact-item">
                  <i className="fas fa-map-marker-alt"></i>
                  <span>Ohenimuri, Walkerville</span>
                </div>
                <div className="contact-item">
                  <i className="fas fa-phone"></i>
                  <a href="tel:0765536048">076 553 6048</a>
                  <span> / </span>
                  <a href="tel:0742686212">074 268 6212</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-envelope"></i>
                  <a href="mailto:info@techxm.co.za">info@techxm.co.za</a>
                </div>
                <div className="contact-item">
                  <i className="fas fa-clock"></i>
                  <span>Mon - Fri: 9:00 - 18:00</span>
                </div>
              </div>
              <div className="footer-newsletter">
                <p className="newsletter-text">Subscribe to our newsletter</p>
                <form className="newsletter-form" onSubmit={handleNewsletterSubmit}>
                  <input
                    type="email"
                    name="newsletterEmail"
                    className="newsletter-input"
                    placeholder="Your email address"
                    required
                  />
                  <button type="submit" className="newsletter-btn">
                    <i className="fas fa-paper-plane"></i>
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; 2025 TECHXM. All rights reserved. Enterprise Digital Solutions.</p>
            <div className="footer-legal">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-of-service">Terms of Service</a>
              <a href="/cookie-policy">Cookie Policy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
